/**
 * Company metrics view-state — never return null from the panel shell.
 */

import { METRICS_RANGES } from "./constants";

export const METRICS_STATES = {
  LOADING: "loading",
  SUCCESS: "success",
  PARTIAL: "partial",
  EMPTY: "empty",
  ERROR: "error",
};

const RANGE_MONTHS = {
  "1Y": 12,
  "3Y": 36,
  "5Y": 60,
  "10Y": 120,
  Max: null,
};

export function resolveMetricsViewState({ loading, error, model }) {
  if (loading) return METRICS_STATES.LOADING;
  if (error && !model) return METRICS_STATES.ERROR;
  if (!model) return METRICS_STATES.EMPTY;

  const hasStatements = (model.statements || []).length > 0;
  const quoteOk = Boolean(model.modules?.quote?.available);
  const incomeOk = Boolean(model.modules?.income_statement?.available);

  if (!hasStatements && !quoteOk) return METRICS_STATES.EMPTY;
  if (model.partial || !incomeOk) return METRICS_STATES.PARTIAL;
  return METRICS_STATES.SUCCESS;
}

export function metricsMissingModules(model) {
  if (!model?.modules) return ["all"];
  return Object.entries(model.modules)
    .filter(([, meta]) => !meta?.available)
    .map(([key]) => key);
}

export function filterSeriesByRange(series = [], range = "5Y") {
  if (!series?.length || range === "Max") return series;
  const months = RANGE_MONTHS[range];
  if (!months) return series;

  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - months);
  return series.filter((row) => row.date && new Date(row.date) >= cutoff);
}

export function attachYoYGrowth(series = []) {
  const sorted = [...(series || [])].sort((a, b) => new Date(a.date) - new Date(b.date));
  return sorted.map((row, idx) => {
    const prev = sorted[idx - 1];
    const prevActual = prev?.actual;
    const yoyGrowth =
      row.actual != null && prevActual != null && prevActual !== 0
        ? ((row.actual - prevActual) / Math.abs(prevActual)) * 100
        : null;
    return { ...row, yoyGrowth };
  });
}

export function mergeMultiLineSeries(seriesMap = {}, metricIds = [], controls = {}) {
  const dateMap = new Map();
  metricIds.forEach((metricId) => {
    const filtered = filterSeriesByRange(seriesMap[metricId] || [], controls.range || "5Y");
    filtered.forEach((row) => {
      const existing = dateMap.get(row.date) || { period: row.period, date: row.date };
      existing[metricId] = row.actual;
      dateMap.set(row.date, existing);
    });
  });
  return Array.from(dateMap.values()).sort((a, b) => new Date(a.date) - new Date(b.date));
}

export function prepareChartSeries(series, { range = "5Y", showYoY = false } = {}) {
  const filtered = filterSeriesByRange(series, range);
  return showYoY ? attachYoYGrowth(filtered) : filtered;
}

export function defaultMetricsControls() {
  return {
    period: "quarter",
    range: "5Y",
    showEstimates: true,
    showYoY: false,
    valueMode: "abs",
    growthMode: "yoy",
    showMedian: true,
  };
}

export const METRICS_RANGE_OPTIONS = METRICS_RANGES;
